import { captureError } from '@clatter/platform';
import { baseApi } from './baseApi';
import { addNotification } from '../store';
import { ServiceTypes } from '../constants';
import { isAbortError } from '../helpers/apiErrors';
import { FolderManagerItemActionNames, FolderManagerItemActionTypes, FolderManagerItemTypes } from '../interfaces';

const mapSlideRepositoryToStore = (item: SlideRepository): Partial<FolderManagerItem<SlideRepository>> => ({
  id: item.ID,
  displayName: item.Name,
  type: FolderManagerItemTypes.SLIDE_REPOSITORY,
  itemData: item,
  slidesCount: item.SlidesCount,
  updatedAt: item.UpdatedAt,
  createdAt: item.CreatedAt,
  deletedAt: item.DeletedAt,
  actions: [
    { name: FolderManagerItemActionNames.EDIT, type: FolderManagerItemActionTypes.SINGLE },
    { name: FolderManagerItemActionNames.SHARE, type: FolderManagerItemActionTypes.SINGLE },
    { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
    { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
  ],
});

export const slideRepositoriesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSlideRepositories: builder.query<Partial<FolderManagerItem<SlideRepository>>[], void>({
      query: () => `/slide-repositories`,
      transformResponse: (result: { data: SlideRepository[] }) => result.data.map(mapSlideRepositoryToStore),
      providesTags: (result) => (result ? [{ type: ServiceTypes.SLIDE_REPOSITORIES, id: 'LIST' }] : []),
    }),

    getSlideRepositoryById: builder.query<SlideRepository, { repositoryId: number }>({
      query: ({ repositoryId }) => `/slide-repositories/${repositoryId}`,
      transformResponse: (result: { data: SlideRepository }) => result.data,
      providesTags: (result, error, { repositoryId }) =>
        result
          ? [
              { type: ServiceTypes.SLIDE_REPOSITORIES, id: repositoryId },
              { type: ServiceTypes.SLIDE_REPOSITORIES, id: 'LIST' },
            ]
          : [],
    }),

    createSlideRepository: builder.mutation<SlideRepository, { data: { name: string } }>({
      query: ({ data }) => ({
        url: `/slide-repositories`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: { data: SlideRepository }) => result.data,
      invalidatesTags: (result) => (result ? [{ type: ServiceTypes.SLIDE_REPOSITORIES, id: 'LIST' }] : []),
    }),

    updateSlideRepository: builder.mutation<
      void,
      { repositoryId: number; data: Partial<{ name: string; description: string | null; thumbnail_asset_version_id: number | null }> }
    >({
      query: ({ repositoryId, data }) => ({
        url: `/slide-repositories/${repositoryId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { repositoryId }) =>
        !error
          ? [
              { type: ServiceTypes.SLIDE_REPOSITORIES, id: 'LIST' },
              { type: ServiceTypes.SLIDE_REPOSITORIES, id: repositoryId },
            ]
          : [],
    }),

    getSlideRepositoryGrants: builder.query<IGrant[], { repositoryId: number }>({
      query: ({ repositoryId }) => `/slide-repositories/${repositoryId}/grants`,
      transformResponse: (result: IApiResponse<IGrant[]>) => result.data,
      providesTags: (result, error, { repositoryId }) => [
        { type: ServiceTypes.SLIDE_REPOSITORIES, id: `GRANTS_${repositoryId}` },
      ],
    }),

    shareSlideRepository: builder.mutation<void, { repositoryId: number; data: SharePayload }>({
      query: ({ repositoryId, data }) => ({
        url: `/slide-repositories/${repositoryId}/grants`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.SLIDE_REPOSITORIES }],
    }),

    revokeSlideRepositoryGrant: builder.mutation<void, { repositoryId: number; data: SharePayload }>({
      query: ({ repositoryId, data }) => ({
        url: `/slide-repositories/${repositoryId}/grants`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.SLIDE_REPOSITORIES }],
    }),

    moveSlideRepositoryToTrash: builder.mutation<any, { repositoryId: number }>({
      query: ({ repositoryId }) => ({
        url: `/slide-repositories/${repositoryId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ repositoryId }, { dispatch, queryFulfilled }) => {
        let deletedItem: ReturnType<typeof mapSlideRepositoryToStore> | undefined;

        const patchResult = dispatch(
          //@ts-ignore
          baseApi.util.updateQueryData('getSlideRepositories', undefined, (draft: any) => {
            const index = draft.findIndex((item: any) => item.id === repositoryId);
            if (index !== -1) {
              deletedItem = { ...draft[index] };
              draft.splice(index, 1);
            }
          }),
        );

        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              type: 'success',
              message: `Slidespace "${deletedItem?.displayName}" has been successfully moved to trash.`,
            }),
          );
        } catch (error) {
          if (isAbortError(error)) return;
          const message = `Couldn't move slidespace "${deletedItem?.displayName}" to trash. Please try again.`;
          patchResult.undo();
          dispatch(addNotification({ type: 'error', message }));
          captureError(error, { message, component: 'slideRepositories.service' });
        }
      },
      invalidatesTags: () => [{ type: ServiceTypes.SLIDE_REPOSITORIES, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSlideRepositoriesQuery,
  useGetSlideRepositoryByIdQuery,
  useCreateSlideRepositoryMutation,
  useUpdateSlideRepositoryMutation,
  useMoveSlideRepositoryToTrashMutation,
  useGetSlideRepositoryGrantsQuery,
  useShareSlideRepositoryMutation,
  useRevokeSlideRepositoryGrantMutation,
} = slideRepositoriesService;
